/**
 * WeightingConfigForm — inline editor for n_ie, n_ds, ie_weight, ds_weight.
 *
 * Client rule: ie_weight + ds_weight must equal 100 before submit.
 * Server 422 is also surfaced on the offending fields.
 *
 * Props:
 *   config      {Object|null}  current config from the API
 *   onSave      {(data)=>Promise<{ok,fieldErrors,userMessage}>}
 *   isLoading   {boolean}
 */

import { useState, useEffect } from 'react';
import Field from '../ui/Field';
import Button from '../ui/Button';

const WeightingConfigForm = ({ config, onSave, isLoading }) => {
  const [form, setForm] = useState({
    n_ie: '3',
    n_ds: '2',
    ie_weight: '50',
    ds_weight: '50',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientError, setClientError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync with loaded config
  useEffect(() => {
    if (config) {
      setForm({
        n_ie: String(config.n_ie ?? ''),
        n_ds: String(config.n_ds ?? ''),
        ie_weight: String(config.ie_weight ?? ''),
        ds_weight: String(config.ds_weight ?? ''),
      });
      setFieldErrors({});
      setClientError(null);
    }
  }, [config]);

  const handleChange = (field, val) => {
    setForm((prev) => ({ ...prev, [field]: val }));
    // Clear field error on edit
    if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    if (clientError) setClientError(null);
  };

  const validate = () => {
    const ie = parseFloat(form.ie_weight);
    const ds = parseFloat(form.ds_weight);
    if (isNaN(ie) || isNaN(ds)) {
      setClientError('Les pondérations doivent être des nombres valides.');
      return false;
    }
    if (Math.round(ie + ds) !== 100) {
      setClientError(`La somme ie_weight (${ie}) + ds_weight (${ds}) doit être égale à 100.`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const result = await onSave({
      n_ie: parseInt(form.n_ie, 10),
      n_ds: parseInt(form.n_ds, 10),
      ie_weight: parseFloat(form.ie_weight),
      ds_weight: parseFloat(form.ds_weight),
    });
    setIsSubmitting(false);

    if (!result.ok) {
      setFieldErrors(result.fieldErrors ?? {});
      if (!Object.keys(result.fieldErrors ?? {}).length) {
        setClientError(result.userMessage);
      }
    } else {
      setFieldErrors({});
      setClientError(null);
    }
  };

  const busy = isSubmitting || isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label="Configuration des pondérations">
      <h3 className="font-montserrat font-semibold text-[#17386E] text-sm">
        Configuration des pondérations
      </h3>

      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Nombre d'IE"
          name="n_ie"
          id="n_ie"
          type="number"
          min="0"
          max="10"
          value={form.n_ie}
          error={fieldErrors.n_ie}
          disabled={busy}
          onChange={(e) => handleChange('n_ie', e.target.value)}
        />
        <Field
          label="Nombre de DS"
          name="n_ds"
          id="n_ds"
          type="number"
          min="0"
          max="10"
          value={form.n_ds}
          error={fieldErrors.n_ds}
          disabled={busy}
          onChange={(e) => handleChange('n_ds', e.target.value)}
        />
        <Field
          label="Poids IE (%)"
          name="ie_weight"
          id="ie_weight"
          type="number"
          min="0"
          max="100"
          step="1"
          value={form.ie_weight}
          error={fieldErrors.ie_weight}
          disabled={busy}
          onChange={(e) => handleChange('ie_weight', e.target.value)}
        />
        <Field
          label="Poids DS (%)"
          name="ds_weight"
          id="ds_weight"
          type="number"
          min="0"
          max="100"
          step="1"
          value={form.ds_weight}
          error={fieldErrors.ds_weight}
          disabled={busy}
          onChange={(e) => handleChange('ds_weight', e.target.value)}
        />
      </div>

      {clientError && (
        <p role="alert" className="text-xs text-red-600">
          {clientError}
        </p>
      )}

      <Button type="submit" variant="secondary" size="sm" disabled={busy}>
        {isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
    </form>
  );
};

export default WeightingConfigForm;
